import {
  CONTENT_ARTICLE_SECTION_TEXT_MAX_LENGTH,
  CONTENT_ARTICLE_TOTAL_TEXT_MAX_LENGTH,
  CONTENT_TITLE_MAX_LENGTH,
} from '@/constants/contentLimits';
import { TArticleSectionDraft } from '@/modules/content/creation/model/types';

// The article rules the backend enforces, mirrored so a creator hears about a problem from
// the field that has it rather than from a 400 the editor cannot attach to anything.
//
// Kept deliberately in step with the mobile app. What is shared is the **rules and the
// copy**, keyed by the four logical fields below — section title, section text, subsection
// title, subsection text. The literal error-key strings are not shared: they identify a
// specific block on screen, and mobile passes messages to its editors as props rather than
// looking them up in a map. When a rule or a message changes here, change it there too.
//
// Required-field rules only apply when publishing; a draft is allowed to be incomplete.
// Length rules always apply, because the backend rejects them on save too.
export const ARTICLE_ERRORS = {
  sectionTitleRequired: 'Section title is required.',
  sectionTextRequired: 'Section text is required.',
  subsectionTitleRequired: 'Sub-section title is required.',
  subsectionTextRequired: 'Sub-section text is required.',

  sectionTitleTooLong: `Section title must be ${CONTENT_TITLE_MAX_LENGTH} characters or fewer.`,
  sectionTextTooLong: `Section text must be ${CONTENT_ARTICLE_SECTION_TEXT_MAX_LENGTH.toLocaleString()} characters or fewer.`,
  subsectionTitleTooLong: `Sub-section title must be ${CONTENT_TITLE_MAX_LENGTH} characters or fewer.`,
  subsectionTextTooLong: `Sub-section text must be ${CONTENT_ARTICLE_SECTION_TEXT_MAX_LENGTH.toLocaleString()} characters or fewer.`,

  noSections: 'Add at least one section before publishing.',
  totalTextTooLong: `The article must be ${CONTENT_ARTICLE_TOTAL_TEXT_MAX_LENGTH.toLocaleString()} characters or fewer in total.`,
} as const;

// The four logical fields the rules above are written against. Both platforms validate
// exactly these, with exactly the messages in ARTICLE_ERRORS.
export type TArticleFieldKind = 'section' | 'subsection';

// Errors are keyed per block, so the editor can put each one under the input that caused it.
// The key carries the kind as well as the id: the id alone would identify the right field on
// screen, but a key that cannot be read without cross-referencing a section list is a poor
// thing to meet in a log line or a failing test.
export const articleFieldKey = (kind: TArticleFieldKind, blockId: string, field: 'title' | 'text') =>
  `article:${kind}:${blockId}:${field}`;

// True when any error belongs to a section or subsection field, so the wizard knows to send
// the creator back to the step that renders them.
export const hasArticleFieldError = (errors: Partial<Record<string, string>>) =>
  Object.keys(errors).some((key) => key.startsWith('article:'));

export type TArticleValidationResult = {
  // Per-field messages, keyed by `articleFieldKey`.
  fields: Record<string, string>;
  // Whole-article problems that belong to no single block.
  article?: string;
  // The first offending block, so the editor can expand and scroll to it.
  firstInvalidBlockId?: string;
};

export const validateArticleSections = (
  sections: TArticleSectionDraft[],
  { requireAll }: { requireAll: boolean },
): TArticleValidationResult => {
  const fields: Record<string, string> = {};
  let firstInvalidBlockId: string | undefined;
  let totalTextLength = 0;

  const fail = (kind: TArticleFieldKind, blockId: string, field: 'title' | 'text', message: string) => {
    const key = articleFieldKey(kind, blockId, field);
    // Keep the first problem found for a field: required beats too-long, and an empty field
    // cannot also be over the limit.
    if (fields[key]) return;
    fields[key] = message;
    firstInvalidBlockId ??= blockId;
  };

  for (const section of sections) {
    if (requireAll && !section.title.trim()) fail('section', section._id, 'title', ARTICLE_ERRORS.sectionTitleRequired);
    if (section.title.length > CONTENT_TITLE_MAX_LENGTH) {
      fail('section', section._id, 'title', ARTICLE_ERRORS.sectionTitleTooLong);
    }

    if (requireAll && !section.text.trim()) fail('section', section._id, 'text', ARTICLE_ERRORS.sectionTextRequired);
    if (section.text.length > CONTENT_ARTICLE_SECTION_TEXT_MAX_LENGTH) {
      fail('section', section._id, 'text', ARTICLE_ERRORS.sectionTextTooLong);
    }

    totalTextLength += section.text.length;

    for (const subsection of section.subsections) {
      if (requireAll && !subsection.title.trim()) {
        fail('subsection', subsection._id, 'title', ARTICLE_ERRORS.subsectionTitleRequired);
      }
      if (subsection.title.length > CONTENT_TITLE_MAX_LENGTH) {
        fail('subsection', subsection._id, 'title', ARTICLE_ERRORS.subsectionTitleTooLong);
      }

      if (requireAll && !subsection.text.trim()) {
        fail('subsection', subsection._id, 'text', ARTICLE_ERRORS.subsectionTextRequired);
      }
      if (subsection.text.length > CONTENT_ARTICLE_SECTION_TEXT_MAX_LENGTH) {
        fail('subsection', subsection._id, 'text', ARTICLE_ERRORS.subsectionTextTooLong);
      }

      totalTextLength += subsection.text.length;
    }
  }

  // Article-level problems, reported once. "No sections at all" outranks the length total:
  // an empty article cannot also be too long.
  let article: string | undefined;
  if (requireAll && sections.length === 0) {
    article = ARTICLE_ERRORS.noSections;
  } else if (totalTextLength > CONTENT_ARTICLE_TOTAL_TEXT_MAX_LENGTH) {
    article = ARTICLE_ERRORS.totalTextTooLong;
  }

  return { fields, article, firstInvalidBlockId };
};
